export const BASE_URL = import.meta.env.VITE_BASE_URL;

// dashboard — follow-up widget endpoints + the v2 "new leads" endpoint.
// (The old v1 dashboard endpoints live behind the server's legacy/ folder and
// are consumed only by the mobile app; the web v2 does not call them.)
export const dashFollowUps = "/core/dashboard/followups";
/** Next due deal-slot on ONE lead — the drawer's "one more deal to follow
 *  up" auto-advance asks this right after a follow-up write. */
export const dashFollowUpNextSlot = "/core/dashboard/followups/next-slot";
export const dashboardFollowUpAnalytics = "/core/dashboard/followup-analytics";
export const dashboardNewLeads = "/core/dashboard/new-leads";
export const dashboardShuffleImpact = "/core/dashboard/shuffle-impact";
export const dashboardShuffleSearch = "/core/dashboard/shuffle-accountability/search";
export const dashboardShuffleAnalytics = "/core/dashboard/shuffle-analytics";
export const dashboardShuffleAnalyticsEvents = "/core/dashboard/shuffle-analytics/events";
export const dashboardAssignmentBreakdown = "/core/dashboard/assignment-breakdown";
export const dashboardAssignmentBreakdownEvents = "/core/dashboard/assignment-breakdown/events";
export const dashboardFilterOptions = "/core/dashboard/filter-options";
export const dashboardLeadDistribution = "/core/dashboard/lead-distribution";
export const dashboardKpiSummary = "/core/dashboard/kpi-summary";
export const dashboardNewLeadsSeries = "/core/dashboard/new-leads-series";
export const dashboardCountryInsights = "/core/dashboard/country-insights";
/** "<Team> members" card — people behind one Team / Country option. */
export const dashboardKeyMembers = "/core/dashboard/key-members";
/** The same card's full view — every Team (or Country) side by side, by stage. */
export const dashboardKeyOverview = "/core/dashboard/key-overview";
export const dashboardMyTeam = "/core/dashboard/my-team";
export const dashboardSourcePerformanceV2 = "/core/dashboard/source-performance-v2";
export const dashboardCallAnalyticsV2 = "/core/dashboard/call-analytics-v2";
export const dashboardBranchPerformanceV2 = "/core/dashboard/branch-performance-v2";
export const dashboardUserRankingV2 = "/core/dashboard/user-ranking-v2";
export const dashboardRevokedLeadsV2 = "/core/dashboard/revoked-leads-v2";
export const dashboardIntakeInsights = "/core/dashboard/intake-insights";
export const dashboardIntakeBreakdown = "/core/dashboard/intake-breakdown";
// Branch-manager cards (Today's tasks / Performance analysis / Team performance):
// ONE endpoint, `view=today|performance|team`; every number is a leads-page count.
export const dashboardBranchManager = "/core/dashboard/branch-manager";
export const dashboardPaymentInsights = "/core/dashboard/payment-insights";
// "Payments by person" — per holder: pending (as of today) + collected (window).
export const dashboardPaymentsByPerson = "/core/dashboard/payments-by-person";
export const dashboardCollections = "/core/dashboard/collections";
export const dashboardPaymentFollowUps = "/core/dashboard/payment-followups";
// "Courses" — the course-like module's items: leads / deals / won (+ money).
export const dashboardCourseInsights = "/core/dashboard/course-insights";
export const dashboardCourseGroupInsights = "/core/dashboard/course-group-insights";
// Recent Activity feed (widget + /activity page). The `-v2` suffix is load-bearing:
// plain /recent-activity is the LEGACY mobile endpoint and returns a different shape.
export const dashboardRecentActivity = "/core/dashboard/recent-activity-v2";
// Custom-report dashboard widgets (saved Report Builder reports pinned as cards).
export const dashboardReportWidgets = "/core/dashboard/report-widgets";
// Global Intake-filter options live in the study-abroad NICHE module (core stays
// industry-agnostic), so this is an /optimized/study-abroad route, not /core.
export const dashboardIntakeOptions =
  "/optimized/study-abroad/dashboard/intake-options";

export const login = "auth/login";
export const logoutEndpoit = "/auth/logout";

// Automation
export const automationEndpoint = "/core/automation";
export const automationTriggers = "/core/automation/triggers";
export const automationActionsCatalog = "/core/automation/actions/catalog";
export const automationGroups = "/core/automation/groups";
export const automationLogs = "/core/automation/logs";
// Rolled-up logs (one row per sweep batch) + the per-group member runs.
export const automationLogsGrouped = "/core/automation/logs/grouped";
export const automationLogGroupRuns = "/core/automation/logs/group-runs";
export const automationShuffleBreakdown =
  "/core/automation/runs/shuffle-breakdown";
// "Run automation" on selected leads (leads page): the rules that can be run
// (`/:id` = what one would do), and the run itself.
export const automationManualRunList = "/core/automation/manual-run/automations";
export const automationManualRun = "/core/automation/manual-run";
// "WhatsApp message sent / received" triggers: every provider's templates in
// one list (the "Templates to watch" picker) + a read-only preview of the
// recent messages a draft trigger would have fired on.
export const automationWhatsappTemplates = "/core/automation/whatsapp-templates";
export const automationWhatsappTemplateScan = "/core/automation/whatsapp-templates/scan";

export const stageDistributionAnalytics = "/core/reports/stage-distribution";
export const perStageAnalytics = "/core/reports/per-stage-analytics";
export const leadStageInsightsEndpoint = "/core/reports/lead-stage-insights";

// Reports v2 router (per-scope cached; envelope { success: true, data }).
export const reportsV2Inactivity = "/core/reports/v2/inactivity";
export const reportsV2AssignmentBreakdown = "/core/reports/v2/assignment-breakdown";
export const reportsV2AssignmentBreakdownEvents = "/core/reports/v2/assignment-breakdown/events";
export const reportsV2DimensionPerformance =
  "/core/reports/v2/dimension-performance";
export const reportsV2CallLeaderboard = "/core/reports/v2/call-leaderboard";
export const reportsV2TopPerformers = "/core/reports/v2/top-performers";
export const reportsV2StatusMatrix = "/core/reports/v2/status-matrix";
export const reportsV2FieldInsights = "/core/reports/v2/field-insights";
// Reports-intl part 2 endpoints (server lands separately — client hooks treat
// a 404 as "not deployed yet" and render a graceful empty state).
export const reportsV2ActivitySummary = "/core/reports/v2/activity-summary";
// Per-user × per-day accountability grid (Team → Daily): new-lead assignments
// + never-attended subset, calls, missed follow-ups.
export const reportsV2UserDailyActivity =
  "/core/reports/v2/user-daily-activity";
export const reportsV2ResponseTime = "/core/reports/v2/response-time";
export const reportsV2Drill = "/core/reports/v2/drill";
export const reportsV2Views = "/core/reports/v2/views";
// Reports-intl part 3 — revenue & engagement + server-rendered section export.
export const reportsV2Revenue = "/core/reports/v2/revenue";
export const reportsV2WhatsappEngagement =
  "/core/reports/v2/whatsapp-engagement";
// Meta ↔ CRM reconciliation drill — per-submission list behind one intake
// bucket (wrong numbers, folded re-enquiries, blocked …).
export const reportsV2MetaIntakeDrill = "/core/reports/v2/meta-intake-drill";
export const reportsV2Export = "/core/reports/v2/export";
// Payment analytics — Revenue sub-tabs Receivables / Deal economics / Data
// quality. Three separate endpoints on purpose: different grains and clocks
// (as-of-today deal book · Child.createdAt cohorts · mixed-grain diagnostics),
// so folding them into /revenue would produce a payload where half the keys
// silently ignore the date picker. Each 404s until the server deploy lands and
// the hooks degrade to `notDeployed`.
export const reportsV2Receivables = "/core/reports/v2/receivables";
// Revenue → By user widgets (performance table / pending-by-user / ranking).
export const reportsV2RevenueUsers = "/core/reports/v2/revenue-users";
// Lead Overview → By user: the per-user work-and-outcome scorecard. Assignment
// cohorts (new / reassigned / re-enquired) with their as-of-today outcomes,
// plus activity, speed, quality and CRM-adoption blocks.
export const reportsV2UserPerformance = "/core/reports/v2/user-performance";
export const reportsV2DealEconomics = "/core/reports/v2/deal-economics";
export const reportsV2PaymentQuality = "/core/reports/v2/payment-quality";
// Reports-intl part 4 — admissions journey + targets/spend (server lands
// separately; hooks treat a 404 as "not deployed yet").
export const reportsV2JourneyFunnel = "/core/reports/v2/journey-funnel";
export const reportsV2JourneyMap = "/core/reports/v2/journey-map";
export const reportsV2TargetAttainment = "/core/reports/v2/target-attainment";
export const reportsV2DocumentsPending = "/core/reports/v2/documents-pending";
// Generic "who's holding these leads" drill tree (Branch→User→Stage→Status→
// Sub-status) — powers the Branch Analytics holder drill.
export const reportsV2LeadTree = "/core/reports/v2/lead-tree";
// Lead Source × holder: leads + enquiries (re-enquiries included) per person.
export const reportsV2SourceCounsellor = "/core/reports/v2/source-counsellor";
// Call-journey outcomes (lead→first-call speed, effort per enrollment,
// re-enquiry split, missed-call recovery).
export const reportsV2CallOutcomes = "/core/reports/v2/call-outcomes";
// Custom report builder (server engine for the custom reports page).
export const reportsV2BuilderRun = "/core/reports/v2/builder/run";
export const reportsV2BuilderList = "/core/reports/v2/builder/list";
export const reportsV2BuilderDrill = "/core/reports/v2/builder/drill";
export const reportsV2BuilderFields = "/core/reports/v2/builder/fields";
export const reportsV2BuilderReports = "/core/reports/v2/builder/reports";
export const reportsV2BuilderExport = "/core/reports/v2/builder/export";
// Monthly goals + marketing spend CRUD (settings UI; envelope { success, data }).
export const targetsEndpoint = "/core/targets";
export const spendEndpoint = "/core/spend";
/** Meta ad set / ad picker options (leads drawer + custom report builder). */
export const metaAdOptionsEndpoint = "/core/meta-ads/options";

export const verifyUserPath = "/auth/verify";
/** "Switch user" (view-as): who I may switch into / mint their token / audit the switch back. */
export const switchUserCandidatesPath = "/auth/switch-user/candidates";
export const switchUserPath = "/auth/switch-user";
export const switchBackPath = "/auth/switch-back";
/** "Check-in / Check-out": the signed-in person's own presence (me / in / out / heartbeat / roles). */
export const checkInEndpoint = "/core/check-in";
export const viewPreferencesPath = "/core/view-preferences";

/** Favorite Views ("SmartViews") — saved leads-filter bundles that become sidebar routes. */
export const favoriteViewsEndpoint = "/core/favorite-views";
export const sharedViewsEndpoint = "/core/shared-views";
export const tenantEndpoints = "/platform/tenant";
export const getTenant = "/platform/tenant/profile";
export const updateTenantEndpoint = "/platform/tenant/update";
export const tenantUsageEndpoint = "/platform/tenant/usage";

// Platform SaaS invoices (Xale billing the tenant)
export const invoiceEndpoint = "/platform/invoice";
export const invoiceDraftEndpoint = "/platform/invoice/draft";
export const invoiceByIdEndpoint = (id: number | string) =>
  `/platform/invoice/${id}`;
export const invoiceStatusEndpoint = (id: number | string) =>
  `/platform/invoice/${id}/status`;

export const childEndpoints = "/core/child";
export const qualificationEdpoints = "/optimized/study-abroad/qualification";
export const createLeadQualificationEndpoint =
  "/optimized/study-abroad/qualification/create-lead-qualification";
export const getQualsByLead =
  "/optimized/study-abroad/qualification/by-lead/:leadId";

export const getStages = "/core/pipeline/stage";
export const getTracks = "/core/tracks/main-track";
/** GET — tracks linked to a stage (StageTracks junction) */
export const getTracksByStagePath = (stageId: number | string) =>
  `/core/tracks/main-track/stage/${stageId}`;
export const getLeadTracks = "/core/tracks/lead-track";
export const createTrackPipeline = "/core/tracks/main-track/pipeline";
export const entityTrackEndpoint = "/core/tracks/lead-track/entity";
export const getAllPipeline = "/core/pipeline/stage/all-pipeline";
export const getStageUsageEndpoint = "/core/pipeline/stage/usage/:id";
export const updateStagePipeline =
  "/core/pipeline/stage/update-stage-pipeline/:id";
export const branchEndpoint = "/core/branches";
export const leadBranchHistoryEndpoint = "/core/branches/lead";

// Branch-based pipelines (Phase 3): fork a branch onto its own pipeline + migrate.
export const branchForkPipelineEndpoint = (id: number | string) =>
  `/core/branches/${id}/pipeline/fork`;
export const branchMigrationPlanEndpoint = (id: number | string) =>
  `/core/branches/${id}/pipeline/migration-plan`;
export const branchMigrationCommitEndpoint = (id: number | string) =>
  `/core/branches/${id}/pipeline/migration/commit`;
export const branchUnforkEndpoint = (id: number | string) =>
  `/core/branches/${id}/pipeline/unfork`;
// Dry-run reverse migration (fork -> tenant default). Same payload shape as
// the forward migration-plan, so the mapping UI is shared.
export const branchUnforkPlanEndpoint = (id: number | string) =>
  `/core/branches/${id}/pipeline/unfork-plan`;
// GET = load the forked pipeline for editing; PUT = save edits (reconcile by id).
export const branchPipelineEndpoint = (id: number | string) =>
  `/core/branches/${id}/pipeline`;
// Saved (archived) fork pipelines: list, restore onto a branch, delete forever.
export const archivedPipelinesEndpoint = "/core/branches/archived-pipelines";
export const archivedPipelineDeleteEndpoint = (pipelineId: number | string) =>
  `/core/branches/archived-pipelines/${pipelineId}`;
export const branchRestorePipelineEndpoint = (id: number | string) =>
  `/core/branches/${id}/pipeline/restore`;

export const addStagePipeline = "/core/pipeline/stage/add-stage-pipeline";
export const deleteStagePipeline = "/core/pipeline/stage/:id";
export const reorderStagePipeline = "/core/pipeline/stage/rearrange-orders";

// Atomic "move every lead + child off this sub-status onto another, then
// drop the source" — wired into the Edit Stage screen's trash button when
// the sub-status still has references. Server: `substatus.service
// .transferAndDelete` (core/pipelines/sub-status).
export const transferAndDeleteSubStatusEndpoint =
  "/core/pipeline/sub-status/:id/transfer-and-delete";

// Same move-and-drop, one level up: every lead + deal on this STATUS is moved
// onto another status (+ one of its sub-statuses) before the source is
// deleted. Server: `status.service.transferAndDelete`.
export const transferAndDeleteStatusEndpoint =
  "/core/pipeline/status/:id/transfer-and-delete";

export const routerMainTabs = "/platform/tabs";
export const userEndpoint = "/platform/users";
export const rolePipeline = "/control/roles/role-pipeline";
export const rolePipelineLite = "/control/roles/role-pipeline/lite";
/** Every tenant role + work-pattern metadata, no users (list role columns). */
export const roleCatalog = "/control/roles/catalog";
export const getAllUsers = "/platform/users";
/** User Management export — column catalog + XLSX download. */
export const usersExportFieldsEndpoint = "/platform/users/export/fields";
export const usersExportEndpoint = "/platform/users/export";
export const roleEndpoint = "/control/roles";
export const leadSourceEndpoint = "/core/source/lead-source";
export const campaignEndpoint = "/core/campaign";
export const campaignFormsEndpoint = "/core/campaign/forms";
/** Sources → Campaigns: a campaign's "Ad sets & ads" tree (lazy, on expand). */
export const campaignAdsTreeEndpoint = (campaignId: number | string) =>
  `/core/campaign/${campaignId}/ads-tree`;
/** The pasted creative / video link on one Meta ad. */
export const metaAdCreativeLinkEndpoint = (adId: number | string) =>
  `/core/meta-ads/${adId}/creative-link`;
/** Web Forms (WEB_FORM sources) — builder palette, saved form, live preview. */
export const webFormsEndpoint = "/core/web-forms";
export const webFormCatalogEndpoint = `${webFormsEndpoint}/catalog`;
export const webFormBySourceEndpoint = `${webFormsEndpoint}/source`;
export const webFormPreviewEndpoint = `${webFormsEndpoint}/preview`;

export const websiteTemplatesEndpoint = "/integrations/website/templates";
export const websiteTestMappingEndpoint = "/integrations/website/test-mapping";
export const websiteTestWebhookEndpoint = "/integrations/website/test-webhook";

export const customInputEndpoint = "/core/prefrence/custom";
export const customInputEndpointItem = "/core/prefrence/custom-item";
export const spesifics = "/core/prefrence/custom/specifics";
export const resolveCustomDefaults =
  "/core/prefrence/custom-item-scope/resolve-defaults";

export const whatsappChatsEndpoint = "/core/source/whatsapp/chats";
/** Every WhatsApp the inbox draws on (Meta numbers + chat sources), for the account switcher. */
export const whatsappInboxAccountsEndpoint = "/core/source/whatsapp/inbox-accounts";
export const whatsappMessagesEndpoint =
  "/core/source/whatsapp/messages/:leadId";
export const sentWhatsappMssgeEndpoint = "/core/source/whatsapp/send/:leadId";
export const sentWhatsappAudioEndpoint =
  "/core/source/whatsapp/send-audio/:leadId";
export const sentWhatsappMediaEndpoint =
  "/core/source/whatsapp/send-media/:leadId";

export const whatsappAccountEndpoint = "/core/source/whatsapp";
export const whatsappTemplatesEndpoint =
  "/core/source/whatsapp/templates/:accountId";
export const whatsappTemplateSyncEndpoint =
  "/core/source/whatsapp/templates/:accountId/sync";
export const whatsappTemplateEditEndpoint =
  "/core/source/whatsapp/templates/edit/:templateId";
export const whatsappTemplateDeleteEndpoint =
  "/core/source/whatsapp/templates/delete/:templateId";
export const whatsappTemplateSendEndpoint =
  "/core/source/whatsapp/templates/send/:leadId/:templateId";
// Lead-scoped, PROVIDER-AWARE. The :accountId routes above are Cloud-API-only
// and return nothing for a BSP (Wati/Gallabox) tenant, which has no account.
export const whatsappLeadTemplatesEndpoint =
  "/core/source/whatsapp/lead-templates/:leadId";
export const whatsappLeadTemplateSendEndpoint =
  "/core/source/whatsapp/lead-templates/:leadId/send";
export const whatsappTemplateUploadMediaEndpoint =
  "/core/source/whatsapp/templates/upload-media/:accountId";

// Wati (automation "Send WhatsApp template") — APPROVED templates on a
// connected Wati lead source; query param: ?sourceId=<leadSourceId>.
export const watiTemplatesEndpoint = "/core/source/wati/templates";

// Gallabox (automation "Send WhatsApp template (Gallabox)") — APPROVED
// templates on a connected Gallabox lead source; query param: ?sourceId=.
export const gallaboxTemplatesEndpoint = "/core/source/gallabox/templates";
// Custom API (outbound) connections: editor meta, send-test, request log.
export const customApiEndpoint = "/core/source/custom-api";
// "Use as WhatsApp" (Webhook / Custom source): the editor's test buttons.
export const whatsappApiEndpoint = "/core/source/whatsapp-api";

// UrbanChat (automation "Send WhatsApp template (UrbanChat)") — APPROVED
// templates on a connected UrbanChat lead source; query param: ?sourceId=.
// Omitting sourceId returns the tenant's connected source (Templates page).
export const urbanchatTemplatesEndpoint = "/core/source/urbanchat/templates";

// Gmail — mounted server-side at /api/v1/gmail and /api/v1/integrations/gmail.
// Sent / Drafts / Templates read EmailMessage rows from Xale. Inbox reads the
// user's own Gmail live (needs the opt-in gmail.readonly grant).
export const gmailMessagesEndpoint = "/gmail/messages";
export const gmailMessageDetailEndpoint = "/gmail/messages/:id";
export const gmailInboxEndpoint = "/gmail/inbox";
export const gmailInboxMessageEndpoint = "/gmail/inbox/:id";
export const gmailInboxAttachmentEndpoint = "/gmail/inbox/:id/attachments/:partId";
export const gmailIntegrationBaseEndpoint = "/integrations/gmail";
export const gmailOAuthStartEndpoint = "/integrations/gmail/oauth/start";
export const gmailMyAccountEndpoint = "/integrations/gmail/accounts/me";
export const gmailListAccountsEndpoint = "/integrations/gmail/accounts";
export const gmailInboxAudienceEndpoint = "/integrations/gmail/accounts/:id/inbox-audience";
export const gmailDisconnectEndpoint = "/integrations/gmail/disconnect/:id";
// Gmail lead capture (Sources → Gmail card): settings, a read-only preview of
// recent lead emails, and the backlog import (dry run unless `apply`).
export const gmailLeadCaptureEndpoint = "/integrations/gmail/lead-capture/:sourceId";
export const gmailLeadCapturePreviewEndpoint = "/integrations/gmail/lead-capture/:sourceId/preview";
export const gmailLeadCaptureBacklogEndpoint = "/integrations/gmail/lead-capture/:sourceId/backlog";

export const emailTemplatesEndpoint = "/email-templates";
export const emailTemplatesSeedDefaultsEndpoint = "/email-templates/seed-defaults";
export const emailTemplateDetailEndpoint = "/email-templates/:id";
export const emailTemplateRenderEndpoint = "/email-templates/:id/render";
export const emailTemplateTestSendEndpoint = "/email-templates/:id/test-send";

// Broadcasts
export const broadcastListEndpoint = "/core/source/whatsapp/broadcasts";
export const broadcastStatsEndpoint = "/core/source/whatsapp/broadcasts/stats";
export const broadcastAudienceCountEndpoint =
  "/core/source/whatsapp/broadcasts/audience-count";
export const broadcastCreateEndpoint = "/core/source/whatsapp/broadcasts";
export const broadcastBspTemplatesEndpoint =
  "/core/source/whatsapp/broadcasts/bsp-templates";
export const broadcastDetailEndpoint = "/core/source/whatsapp/broadcasts/:id";
export const broadcastCancelEndpoint =
  "/core/source/whatsapp/broadcasts/:id/cancel";
export const broadcastDeleteEndpoint = "/core/source/whatsapp/broadcasts/:id";

export const leadsEndpoints = "/core/leads";
// Tenant-wide "Add Lead" required-fields policy (GET any user; PUT super-admin).
export const leadFormConfigEndpoint = "/core/leads/form-config";
/** Tenant-wide lead LOOKUP that ignores read scope — org-gated, contact-free. */
export const globalLeadSearchEndpoint = "/core/leads/global-search";
/** Filter-aware total count, fired in parallel with the leads list. */
export const leadsListCountEndpoint = "/core/leads/list-count";
/** Filter-aware basic-stats matrix, fired in parallel with the leads list. */
export const leadsListStatsEndpoint = "/core/leads/list-stats";
/** Filter-aware per-country lead counts for the header "Pick country" picker. */
export const leadsCountryStatsEndpoint = "/core/leads/country-stats";
/** Filter-aware stage / status / sub-status counts for the header pipeline pickers. */
export const leadsPipelineStatsEndpoint = "/core/leads/pipeline-stats";
/** Per-user visibility audit — drives the red banner above the leads grid. */
export const leadsVisibilityAuditEndpoint = "/core/leads/visibility-audit";
/** Tenant-wide visibility audit (admin only) — banner aggregation for no-filter / branch-filter views. */
export const leadsVisibilityAuditTenantEndpoint =
  "/core/leads/visibility-audit/tenant";
/** Suggest concrete fixes for one user's hidden leads (admin only). */
export const leadsVisibilityFixesAnalyzeEndpoint =
  "/core/leads/visibility-audit/fixes";
/** Apply a chosen visibility fix (admin only). */
export const leadsVisibilityFixesApplyEndpoint =
  "/core/leads/visibility-audit/fixes/apply";
export const leadsExportEndpoint = "/core/leads/export";
export const leadsExportFieldsEndpoint = "/core/leads/export/fields";
export const leadsExportCountEndpoint = "/core/leads/export/count";
export const leadsBoardEndpoint = "/core/leads/board";
/** Paginated slice for one kanban column; same filters as board + `skip` / `take` */
export const leadsBoardColumnEndpoint = (statusId: number | string) =>
  `/core/leads/board/column/${statusId}`;
export const leadsStageBoardEndpoint = "/core/leads/stage-board";
export const leadsStatusSwitchEndpoint = "/core/leads/status-switch";
export const assignLeadEndpoint = "/core/leads/assign";
export const moveAssignmentEndpoint = "/core/leads/assign/move";
export const unassignAssignmentEndpoint = "/core/leads/assign/unassign";
// Peer transfer — hand a lead/deal you hold to a same-role peer (self-revoke).
export const peerEligibilityEndpoint = "/core/leads/assign/peer-eligibility";
export const peerTransferEndpoint = "/core/leads/assign/peer-transfer";
export const assignPreviewEndpoint = (leadId: number | string) =>
  `/core/leads/${leadId}/assign/preview`;
export const checkAssignConflictsEndpoint =
  "/core/leads/assign/check-conflicts";
export const assignEligibilityEndpoint = "/core/leads/assign/eligibility";
export const branchTransferEndpoint = "/core/leads/branch-transfer";
export const branchMismatchLeadsEndpoint = "/core/leads/branch-mismatch";
export const duplicateRolesLeadsEndpoint = "/core/leads/duplicate-roles";
export const bulkStageUpdateEndpoint = "/core/leads/bulk-stage-update";
export const bulkFieldUpdateEndpoint = "/core/leads/bulk-field-update";
export const bulkArchiveLeadsEndpoint = "/core/leads/bulk-archive";
export const bulkJobStatusEndpoint = (jobId: string) =>
  `/core/leads/bulk-jobs/${jobId}`;
export const assignChildEndpoint = "/core/child/assign";

export const dashboardLeadsByCourse =
  "/optimized/study-abroad/dashboard/leads-by-course";
export const dashboardLeadInsights =
  "/optimized/study-abroad/dashboard/lead-insights";
export const dashboardCountryPerformance =
  "/optimized/study-abroad/dashboard/country-performance";
export const dashboardCustomFieldInsights =
  "/optimized/study-abroad/dashboard/custom-field-insights";
export const universityEndpoint = "/optimized/study-abroad/univ";
export const courseEndpoints = "/optimized/study-abroad/course";
export const scholarshipEndpoints = "/optimized/study-abroad/course";
export const savedCourseEndpoint = "/optimized/study-abroad/saved-course";
export const savedCourseByLeadEndpoint = (leadId: number | string) =>
  `/optimized/study-abroad/saved-course/by-lead/${leadId}`;
export const savedCourseDeleteEndpoint = (
  leadId: number | string,
  courseId: number | string,
) => `/optimized/study-abroad/saved-course/${leadId}/${courseId}`;
export const moduleConnect = "/platform/connect";
export const doc_upload = "/core/doc/upload";
/** Direct-to-Spaces upload: presign → browser PUTs to Spaces → complete */
export const doc_presign = "/core/doc/presign";
export const doc_upload_complete = "/core/doc/upload-complete";
export const doc_endpoint = "/core/doc";
/** GET/POST /core/doc/folders — Drive-style attachment folders */
export const doc_folders_endpoint = "/core/doc/folders";
/** PATCH/DELETE /core/notes/:id — author edit / WhatsApp-style soft delete */
export const note_endpoint = "/core/notes";
/** Stage/status/substatus notification tones (≤1MB audio, role-wise) */
export const notification_tones_endpoint = "/core/notification-tones";

export const req_mutations = "/core/requirements";
export const req_by_stage = "/core/requirements/by-stage";
/** Track-status requirements — the track twin of the two above. */
export const track_req_mutations = "/core/tracks/requirements";
export const track_req_by_track = "/core/tracks/requirements/by-track";

export const connectorByIndustry = "/platform/connect/by-industry";

export const univ_spesific_key = "add-univ";
export const course_spesific_key = "add-course";
export const lead_spesific_key = "lead-management";
export const child_spesific_key = "child-management";
export const user_spesific_key = "user_spesific";

export const internalChatEndpoint = "/core/internal-chat";

export const activityLogEndpoint = "/core/activity-log";
export const callLogEndpoint = "/core/call-logs";
export const industry_endpoint = "platform/industry";

// Video Production (niche)
export const videoProductionServiceEndpoint =
  "/optimized/video-production/video-service";
export const videoProductionVariantEndpoint =
  "/optimized/video-production/video-service-variants";
export const videoProductionModelsEndpoint =
  "/optimized/video-production/video-models";
export const videoProductionAmbientEndpoint =
  "/optimized/video-production/video-ambient";

// Payments
export const paymentEndpoint = "/core/payment";

// EMI / instalment plans. Sibling of paymentEndpoint — the schedule lives
// beside the ledger it settles.
export const installmentEndpoint = "/core/installment";

// Coupon codes — admin CRUD (`/`), the counsellor's check (`/validate`) and the
// Settings scope picker (`/scope-options`). Apply / remove live on the deal:
// `${childEndpoints}/:id/coupon`.
export const couponEndpoint = "/core/coupons";

// Razorpay (per-tenant payment gateway)
export const razorpayIntegrationEndpoint = "/integrations/razorpay";
// Connected tools: third-party tools plugged into leads, described by data.
export const connectedToolsEndpoint = "/integrations/tools";
export const razorpayConnectEndpoint = "/integrations/razorpay/connect";
export const razorpayDisconnectEndpoint = "/integrations/razorpay/disconnect";
export const razorpaySettingsEndpoint = "/integrations/razorpay/settings";
export const razorpayPaymentLinksEndpoint = "/integrations/razorpay/payment-links";
export const razorpayWhatsappSenderEndpoint = "/integrations/razorpay/whatsapp-sender";
export const razorpayWebhookSetupEndpoint = "/integrations/razorpay/webhook-setup";
export const razorpayPaymentLinkSyncEndpoint = (id: number | string) =>
  `/integrations/razorpay/payment-links/${id}/sync`;
export const razorpayPaymentLinkCancelEndpoint = (id: number | string) =>
  `/integrations/razorpay/payment-links/${id}/cancel`;
export const razorpayPaymentLinkSendEndpoint = (id: number | string) =>
  `/integrations/razorpay/payment-links/${id}/send`;
export const razorpayPaidMoveEndpoint = "/integrations/razorpay/paid-move";

// Academy (niche)
export const academyProgramEndpoint = "/optimized/academy/program";
export const academyProgramVariantEndpoint =
  "/optimized/academy/program-variant";

// Tenant Features
export const tenantFeatureEndpoint = "/platform/tenant-feature";

// External Access (generic - featureKey-driven)
export const externalAccessStatusEndpoint = (featureKey: string, leadId: number | string) =>
  `/platform/external-access/${featureKey}/status/${leadId}`;
export const externalAccessGenerateEndpoint = (featureKey: string, leadId: number | string) =>
  `/platform/external-access/${featureKey}/generate/${leadId}`;
export const externalAccessResetEndpoint = (featureKey: string, leadId: number | string) =>
  `/platform/external-access/${featureKey}/reset-password/${leadId}`;
export const externalAccessToggleEndpoint = (featureKey: string, leadId: number | string) =>
  `/platform/external-access/${featureKey}/toggle/${leadId}`;
export const externalLoginEndpoint = "/auth/external/login";
export const externalVerifyEndpoint = "/auth/external/verify";

// Secondary DB (Frontend LocalStorage Alternative)
// Acts as a cloud-synced, multi-tenant Key-Value store using PostgreSQL JSONB.
// Endpoints available under secondaryDbEndpoint:
// 1. POST / -> Create new record: { key: "some_key", data: { ...anyJson } }
// 2. GET /:key -> Fetch record by key
// 3. PATCH /:key -> Update existing record: { data: { ...newJson } }
// 4. DELETE /:key -> Delete record by key
// 5. GET / -> Get all records
export const secondaryDbEndpoint = "/platform/secondary-db";

// Notifications (inbox)
export const notificationEndpoint = "/core/notifications";
/** Alerts — admin/manager notices to the roles under them (Alert box + popup). */
export const alertEndpoint = "/core/alerts";
/** "Please assign this lead" — asks a person or a role through an alert that pops until Done. */
export const assignmentRequestEndpoint = "/core/assignment-requests";

// Connected devices (Settings → Connected Devices; mobile app registers itself)
export const connectedDevicesEndpoint = "/core/devices";
export const connectedDeviceDeleteEndpoint = (id: number | string) =>
  `/core/devices/${id}`;

// System Logs (tenant-wide operational/audit log; admin-only)
export const systemLogEndpoint = "/core/system-log";
export const systemLogSourcesEndpoint = "/core/system-log/sources";
export const systemLogCountEndpoint = "/core/system-log/count";
export const systemLogExportEndpoint = "/core/system-log/export";

// Client integrity probe (report-only; see utils/clientIntegrity.ts)
export const clientIntegrityEndpoint = "/core/security/client-integrity";

// Tenant snapshots
export const snapshotsEndpoint = "/core/snapshots";
// Cross-tenant admin list (platform staff / system admin only)
export const tenantSnapshotsAdminEndpoint = "/core/snapshots/admin";

// Tenant custom domains (white-label routing). Tenant-admin gated.
export const tenantDomainsEndpoint = "/core/tenant-domains";
export const tenantDomainVerifyEndpoint = (id: number | string) =>
  `/core/tenant-domains/${id}/verify`;
export const tenantDomainPrimaryEndpoint = (id: number | string) =>
  `/core/tenant-domains/${id}/primary`;
export const tenantDomainDeleteEndpoint = (id: number | string) =>
  `/core/tenant-domains/${id}`;

// Public — no auth, used by login/landing surfaces to brand themselves
// based on the request's hostname.
export const publicTenantByHostnameEndpoint = "/public/tenant-by-hostname";

export function getAvatarUrl(name: string): string {
  // Generate a simple avatar url using UI Avatars
  const baseUrl = "https://ui-avatars.com/api/";
  const encodedName = encodeURIComponent(name);
  return `${baseUrl}?name=${encodedName}&background=random&color=fff`;
}

/** The two "Today's Tasks" cards' configured tiles (admin-saved leads filters). */
export const dashboardTaskTiles = "/core/dashboard/task-tiles";
// One Today's Tasks tile split by branch (+ people) / Country & key fields / person.
export const dashboardTaskTileBreakdown = "/core/dashboard/task-tiles/breakdown";

/** Every pipeline (default + branch forks) with its stage tree — the role editor. */
export const getAllPipelines = "/core/pipeline/stage/all-pipelines";
// Razorpay page (admin console over the connected account)
export const razorpayConsoleRegistryEndpoint = "/integrations/razorpay/console/registry";
export const razorpayConsoleOverviewEndpoint = "/integrations/razorpay/console/overview";
export const razorpayConsoleAuditEndpoint = "/integrations/razorpay/console/audit";
export const razorpayConsoleXaleLinksEndpoint = "/integrations/razorpay/console/xale-links";
export const razorpayConsoleOpEndpoint = (opId: string) =>
  `/integrations/razorpay/console/ops/${encodeURIComponent(opId)}`;
export const razorpayConsoleOpUploadEndpoint = (opId: string) =>
  `${razorpayConsoleOpEndpoint(opId)}/upload`;
export const razorpayConsolePresetsEndpoint = "/integrations/razorpay/console/presets";
export const razorpayConsolePresetEndpoint = (id: number | string) =>
  `${razorpayConsolePresetsEndpoint}/${id}`;
